import Feedback from '@components/layout/main/Feedback';
import ThinkingOverview from '@components/layout/main/ThinkingOverview';
import { useWordStream } from '@hooks';
import type { User } from '@platform2-lib/models/models';
import { useShellStore } from '@store/shell';
import type { ForwardedRef, MouseEvent, PropsWithChildren } from 'react';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import type { SourcesOverviewResponse } from 'types/schema';
import Styled from './ChatMessage.styled';

interface Props extends PropsWithChildren {
  children: string;
  className?: string;
  ['data-testid']?: string;
  id?: string;
  shouldShowFeedback?: boolean;
  shouldShowThinkingOverview?: boolean;
  shouldStreamText?: boolean;
  user?: User;
  viewportHeight?: number;
  onHover?: () => void;
  onLoadThinkingOverview?: (data: SourcesOverviewResponse) => void;
}

function ChatMessage(
  {
    children,
    className,
    ['data-testid']: testId,
    id,
    shouldShowFeedback,
    shouldShowThinkingOverview,
    shouldStreamText,
    user,
    viewportHeight = 0,
    onHover,
    onLoadThinkingOverview
  }: Props,
  ref: ForwardedRef<HTMLDivElement>
) {
  const setToast = useShellStore((state) => state.setToast);
  const [wordStream, isStreamComplete] = useWordStream(
    children,
    shouldStreamText ? 25 : 0,
    !shouldStreamText
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const echoRef = useRef<HTMLDivElement>(null);
  const [echoHasOverflow, setEchoHasOverflow] = useState(false);
  const [isEchoExpanded, setIsEchoExpanded] = useState(false);

  useImperativeHandle(ref, () => wrapperRef.current);

  useEffect(() => {
    const { current: echoElement } = echoRef;

    if (echoElement) {
      setEchoHasOverflow(echoElement.scrollHeight > echoElement.clientHeight);
    }

    wrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleCopyText = () => {
    navigator.clipboard.writeText(children);
    setToast({ message: 'Copied to clipboard!' });
  };

  const handleSeeMore = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsEchoExpanded((isExpanded) => !isExpanded);
    window.requestAnimationFrame(() => {
      if (!isEchoExpanded) {
        // Scroll to the top of the echo if it is collapsed
        wrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  return (
    <Styled.Wrapper
      className={className}
      data-testid={testId}
      ref={wrapperRef}
      viewportHeight={viewportHeight}
      onMouseOver={onHover}>
      {user ? (
        <Styled.EchoWrapper className="echo" data-testid="user-message">
          <Styled.Echo
            expanded={echoHasOverflow ? isEchoExpanded : undefined}
            ref={echoRef}
            onClick={handleCopyText}>
            <p>{children.split('__')[0]}</p>
            {echoHasOverflow && (
              <Styled.SeeMoreButton
                data-testid="see-more-button"
                onClick={handleSeeMore}>
                See {isEchoExpanded ? 'less' : 'more'}
              </Styled.SeeMoreButton>
            )}
          </Styled.Echo>
        </Styled.EchoWrapper>
      ) : (
        <>
          {shouldShowThinkingOverview && (
            <ThinkingOverview replyId={id} onLoad={onLoadThinkingOverview} />
          )}
          <Styled.AgentMessage
            className="agent-message"
            data-testid="agent-message">
            {wordStream}
          </Styled.AgentMessage>
          {shouldShowFeedback && (!shouldStreamText || isStreamComplete) && (
            <Feedback />
          )}
        </>
      )}
    </Styled.Wrapper>
  );
}

export default memo(forwardRef(ChatMessage));
